import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Trophy, Calendar, BarChart3, FileText, Settings } from 'lucide-react';
import { VenueGameSubmissionForm } from '@/components/venue/VenueGameSubmissionForm';
import { VenueEventSubmissionForm } from '@/components/venue/VenueEventSubmissionForm';
import { VenueUpcomingGames } from '@/components/venue/VenueUpcomingGames';
import { VenueUpcomingEvents } from '@/components/venue/VenueUpcomingEvents';
import { VenueGamesDashboard } from '@/components/venue/VenueGamesDashboard';
import { VenueAnalyticsDashboard } from '@/components/venue/VenueAnalyticsDashboard';
import { VenueProfileSetup } from '@/components/venue/VenueProfileSetup';
import { CustomerFeedbackDisplay } from '@/components/venue/CustomerFeedbackDisplay';
import { SeasonArchiveManager } from '@/components/retention/SeasonArchiveManager';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import Header from '@/components/Header';

export default function VenueTools() {
  const { user: supabaseUser } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const user = supabaseUser || internalUser;
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('games');

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  if (!user) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-background pt-20">
          <div className="container mx-auto px-4 py-8">
            <Card className="max-w-md mx-auto">
              <CardContent className="p-8 text-center">
                <p className="text-muted-foreground">
                  Please log in to access venue tools.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </>
    );
  }

  if (user.role !== 'venue') {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-background pt-20">
          <div className="container mx-auto px-4 py-8">
            <Card className="max-w-md mx-auto">
              <CardContent className="p-8 text-center">
                <p className="text-muted-foreground">
                  This page is only accessible to venue managers.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-black pt-20">
        <div className="container mx-auto px-4 py-8 space-y-8">
          {/* Page Header */}
          <div className="text-center space-y-4">
            <h1 className="text-4xl font-bold text-white">Venue Tools</h1>
            <p className="text-gray-300 text-lg max-w-2xl mx-auto">
              Manage your venue games, events, player rosters, and review scorekeeping submissions
            </p>
          </div>

          {/* Main Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-5 h-12 bg-gray-900 border-gray-700">
              <TabsTrigger 
                value="games" 
                className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-black text-gray-300 hover:text-white"
              >
                <Trophy className="w-4 h-4" />
                <span className="hidden sm:inline">Games & Players</span>
                <span className="sm:hidden">Games</span>
              </TabsTrigger>
              <TabsTrigger 
                value="events" 
                className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-black text-gray-300 hover:text-white"
              >
                <Calendar className="w-4 h-4" />
                Events
              </TabsTrigger>
              <TabsTrigger 
                value="requests" 
                className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-black text-gray-300 hover:text-white"
              >
                <FileText className="w-4 h-4" />
                Requests
              </TabsTrigger>
              <TabsTrigger 
                value="stats" 
                className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-black text-gray-300 hover:text-white"
              >
                <BarChart3 className="w-4 h-4" />
                Analytics
              </TabsTrigger>
              <TabsTrigger 
                value="setup" 
                className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-black text-gray-300 hover:text-white"
              >
                <Settings className="w-4 h-4" />
                Setup
              </TabsTrigger>
            </TabsList>

            {/* Games & Players Tab - Main management interface */}
            <TabsContent value="games" className="space-y-8 mt-6">
              {/* Game Dashboard - Shows games, players, rosters, score management */}
              <Card className="bg-gray-900 border-gray-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-white">
                    <Trophy className="w-5 h-5 text-primary" />
                    Game & Player Management
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <VenueGamesDashboard venueId={user.id} />
                </CardContent>
              </Card>

              {/* Game Submission Form */}
              <Card className="bg-gray-900 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white">Submit New Game</CardTitle>
                </CardHeader>
                <CardContent>
                  <VenueGameSubmissionForm 
                    venueId={user.id} 
                    onSuccess={() => window.location.reload()} 
                  />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Events Tab */}
            <TabsContent value="events" className="space-y-6 mt-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="bg-gray-900 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-white">Submit New Event</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <VenueEventSubmissionForm 
                      venueId={user.id} 
                      onSuccess={() => window.location.reload()} 
                    />
                  </CardContent>
                </Card>

                <VenueUpcomingEvents venueId={user.id} />
              </div>
            </TabsContent>

            {/* Requests Tab */}
            <TabsContent value="requests" className="space-y-6 mt-6">
              <Card className="bg-gray-900 border-gray-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-white">
                    <FileText className="w-5 h-5 text-primary" />
                    Pending Approval Requests
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <VenueGamesDashboard venueId={user.id} showPendingOnly={true} />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Analytics Tab */}
            <TabsContent value="stats" className="space-y-6 mt-6">
              <VenueAnalyticsDashboard venueId={user.id} />
              <CustomerFeedbackDisplay venueId={user.id} />
              <SeasonArchiveManager entityId={user.id} entityType="venue" />
            </TabsContent>

            {/* Setup Tab */}
            <TabsContent value="setup" className="space-y-6 mt-6">
              <VenueProfileSetup venueId={user.id} />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </>
  );
}